import React, { useEffect, useState } from 'react'
import { generateContext } from 'parkdepot-shared/utils/contextGenerator'
import { FormData, PreflightData } from './types'
import { FC } from 'react'
import { useCustomerAPI } from './hooks'
import { useHistory } from 'react-router-dom'
import { guestFinalRoute } from './route'
import { useAuth } from '@context/AuthContext/AuthContext'
import { DateTime, Duration } from 'luxon'

export type AplyWhitelistResult = 'SUCCESS' | 'ERROR' | 'EMAIL_NOT_SENT'

export type GuestContextType = {
    preflightData?: PreflightData
    endDateFormatted?: string
    durationFormatted?: string
    formData?: FormData
    updateFormData: (data: FormData) => void
    setPreflightData: (data: PreflightData) => void
    loading?: boolean
    setLoading: (loading: boolean) => void
    error?: string
    setError: (error: string) => void
    token?: string
    setToken: (token: string) => void
    applyWhiteList: () => Promise<void>
    applyWhitelistResult?: AplyWhitelistResult
    setApplyWhitelistResult: (result: AplyWhitelistResult) => void
}

export const [GuestContext, GuestContextProvider] = generateContext<GuestContextType>()

export const GuestContextContainer: FC = ({ children }) => {
    const [preflightData, setPreflightData] = useState<PreflightData>()
    const { user } = useAuth()
    const [formData, setFormData] = useState<FormData>({ country: 'INT' })
    const [token, setToken] = useState<string>()
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string>()
    const [applyWhitelistResult, setApplyWhitelistResult] = useState<AplyWhitelistResult>()
    const { addCustomerWhitelist, sendEmail } = useCustomerAPI()
    const history = useHistory()

    const applyWhiteList = async () => {
        if (!formData?.country || !formData?.plate) return
        setLoading(true)
        history.push(guestFinalRoute())

        try {
            await addCustomerWhitelist({
                email: formData?.email,
                plate: formData.plate,
                country: formData.country
            })
        } catch (error) {
            console.error('Error applying whitelist:', error)
            setApplyWhitelistResult('ERROR')
            setLoading(false)
            return
        }

        if (!formData.email) {
            setApplyWhitelistResult('EMAIL_NOT_SENT')
            setLoading(false)
            return
        }

        try {
            await sendEmail({
                plate: formData.plate,
                email: formData.email
            })
        } catch (error) {
            setApplyWhitelistResult('EMAIL_NOT_SENT')
            setLoading(false)
            console.error('Error sending email:', error)
            return
        }

        setApplyWhitelistResult('SUCCESS')
        setLoading(false)
    }

    const updateFormData = (data: FormData) => {
        setFormData((prevData) => ({
            ...prevData,
            ...data
        }))
    }

    // prefill form data with user data if available
    useEffect(() => {
        const lp = user?.license_plates?.[0]
        if (lp) {
            setFormData({
                country: lp?.country || 'INT',
                plate: lp?.plate,
                email: user?.email
            })
        }
    }, [user])

    return (
        <GuestContextProvider
            context={{
                preflightData,
                token,
                setPreflightData,
                setToken,
                loading,
                setLoading,
                error,
                setError,
                formData,
                updateFormData,
                applyWhiteList,
                applyWhitelistResult,
                setApplyWhitelistResult,
                // TODO handle cases where end date is not today
                endDateFormatted: preflightData?.endDate
                    ? DateTime.fromISO(preflightData?.endDate).toFormat('HH:mm')
                    : undefined,
                durationFormatted: preflightData?.whitelist_duration
                    ? Duration.fromISO(preflightData?.whitelist_duration).toFormat('h')
                    : undefined
            }}
        >
            {children}
        </GuestContextProvider>
    )
}
